import { collection, doc, getDoc, getDocs, query, where, orderBy, updateDoc, deleteDoc, limit, startAfter } from 'firebase/firestore';
import { db } from '../../data/base';

/**
 * Servizio per gestire le operazioni sui cataloghi e i loro dettagli
 */

/**
 * Recupera i dettagli di un catalogo
 * @param {string} catalogueId - ID del catalogo
 * @returns {Promise<Object|null>} Dati del catalogo o null se non trovato
 */
export const getCatalogueDetails = async (catalogueId) => {
    try {
        const catalogueRef = doc(db, "Catalogues", catalogueId);
        const catalogueSnap = await getDoc(catalogueRef);

        if (catalogueSnap.exists()) {
            return { id: catalogueSnap.id, ...catalogueSnap.data() };
        }
        return null;
    } catch (error) {
        console.error("Error retrieving catalogue details:", error);
        throw error;
    }
};

/**
 * Recupera tutti i prodotti di un catalogo per il riordinamento
 * @param {string} catalogueId - ID del catalogo
 * @returns {Promise<Array>} Array dei prodotti
 */
export const getAllProductsForReordering = async (catalogueId) => {
    try {
        const productsQuery = query(
            collection(db, "CataloguesProducts"),
            where("catalogRef", "==", catalogueId),
            where("status", "==", "Pubblicato"),
            orderBy("priority", "asc"),
            orderBy("lastUpdate", "desc")
        );

        const querySnapshot = await getDocs(productsQuery);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            modelName: doc.data().modelName || doc.data().nome_modello || 'Unknown Model',
            imgUrl: doc.data().imgUrl || doc.data().image || null
        }));
    } catch (error) {
        console.error("Error fetching all products for reordering:", error);
        throw error;
    }
};

/**
 * Recupera i prodotti di un catalogo con paginazione
 * @param {string} catalogueId - ID del catalogo
 * @param {number} page - Numero di pagina
 * @param {number} size - Dimensione della pagina
 * @param {boolean} applyAdultFilter - Se applicare il filtro adulti
 * @returns {Promise<Object>} Oggetto con prodotti, totale e brand
 */
export const getCatalogueProducts = async (catalogueId, page = 1, size = 20, lastVisible = null, applyAdultFilter = false) => {
    try {
        // Query base per i prodotti del catalogo
        let baseConditions = [
            where("catalogRef", "==", catalogueId),
            where("status", "==", "Pubblicato")
        ];

        // Se il filtro adulti è attivo, aggiungi la condizione
        if (applyAdultFilter) {
            baseConditions.push(where("tags", "array-contains", "ADULT"));
        }

        // Query per il conteggio totale (esegui una volta sola, se necessario)
        const countQuery = query(collection(db, "CataloguesProducts"), ...baseConditions);
        const countSnapshot = await getDocs(countQuery);
        const totalCount = countSnapshot.size;

        // Query per i prodotti paginati
        let productsQuery = query(
            collection(db, "CataloguesProducts"),
            ...baseConditions,
            orderBy("priority", "asc"),
            // orderBy("lastUpdate", "desc"), // Rimosso temporaneamente per debug indice
            limit(size)
        );

        // Aggiungi il cursore per le pagine successive
        if (page > 1 && lastVisible) {
            productsQuery = query(
                collection(db, "CataloguesProducts"),
                ...baseConditions,
                orderBy("priority", "asc"),
                // orderBy("lastUpdate", "desc"), // L'ordinamento deve essere presente anche qui
                startAfter(lastVisible),
                limit(size)
            );
        }

        const querySnapshot = await getDocs(productsQuery);
        const productsData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            modelName: doc.data().modelName || doc.data().nome_modello || 'Unknown Model',
            imgUrl: doc.data().imgUrl || doc.data().image || null
        }));

        // Recupera l'ultimo documento visibile per la paginazione successiva
        const newLastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

        // Raccogli i riferimenti ai brand dai prodotti caricati
        const brandRefs = new Set(productsData.map(p => p.mainBrandRef).filter(Boolean));

        // Recupera i dati dei brand
        const brandsMap = {};
        if (brandRefs.size > 0) {
            const brandPromises = Array.from(brandRefs).map(async (brandRef) => {
                try {
                    const brandDoc = await getDoc(doc(db, "MainBrands", brandRef));
                    if (brandDoc.exists()) {
                        brandsMap[brandRef] = brandDoc.data().nameBrand || brandDoc.data().nome_brand || 'Unknown Brand';
                    }
                } catch (error) {
                    console.error(`Error fetching brand ${brandRef}:`, error);
                }
            });
            await Promise.all(brandPromises);
        }

        // Associa i nomi dei brand ai prodotti
        const validProducts = productsData.map(product => ({
            ...product,
            brandName: product.mainBrandRef ? brandsMap[product.mainBrandRef] : 'Unknown Brand',
            isVisible: product.isVisible !== false
        }));

        return {
            products: validProducts,
            totalCount,
            lastVisible: newLastVisible
        };
    } catch (error) {
        console.error("Error loading catalogue products:", error);
        throw error;
    }
};

/**
 * Recupera le varianti per un array di prodotti
 * @param {Array} productsData - Array dei prodotti
 * @returns {Promise<Object>} Oggetto con varianti organizzate per prodotto
 */
export const getProductVariants = async (productsData) => {
    try {
        // Raccogliamo tutti gli ID delle varianti dai prodotti
        const allVariantIds = [];
        const productVariantMap = {};

        productsData.forEach(product => {
            if (product.list_variants && product.list_variants.length > 0) {
                allVariantIds.push(...product.list_variants);
                productVariantMap[product.id] = product.list_variants;
            }
        });

        if (allVariantIds.length === 0) {
            return {};
        }

        // Organizza le varianti per prodotto
        const variantsByProduct = {};

        // Inizializza gli array delle varianti per ogni prodotto
        productsData.forEach(product => {
            variantsByProduct[product.id] = [];
        });

        // Carica le varianti in batch per migliorare le prestazioni
        const batchSize = 10;
        for (let i = 0; i < allVariantIds.length; i += batchSize) {
            const batch = allVariantIds.slice(i, i + batchSize);
            const variantPromises = batch.map(async (variantId) => {
                try {
                    const variantRef = doc(db, "CataloguesVariants", variantId);
                    const variantSnap = await getDoc(variantRef);

                    if (variantSnap.exists()) {
                        const data = variantSnap.data();

                        // Trova il prodotto a cui appartiene questa variante
                        const productId = Object.keys(productVariantMap).find(pid =>
                            productVariantMap[pid].includes(variantId)
                        );

                        if (productId) {
                            // Normalizza lo stato della variante
                            let normalizedStatus = 'Published';
                            if (data.status) {
                                const status = data.status.toLowerCase();
                                if (status === 'draft' || status === 'bozza') {
                                    normalizedStatus = 'Draft';
                                }
                            }

                            // Crea l'oggetto variante
                            const variant = {
                                id: variantSnap.id, // Usa l'ID del documento Firebase
                                productId: productId,
                                ...data,
                                poster: data.poster || data.image || null,
                                glassesName: data.glassesName || data.name || 'Unknown Variant',
                                frameColor: data.frameColor || data.color || 'Unknown Color',
                                lensesColor: data.lensesColor || data.lensColor || 'TRANSPARENT',
                                skuModel: data.sku || data.skuModel || '',
                                batch: data.batch || '',
                                size: data.size || { size: '' },
                                status: normalizedStatus
                            };

                            // Aggiungi la variante all'array del prodotto corrispondente
                            variantsByProduct[productId].push(variant);
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching variant ${variantId}:`, error);
                }
            });

            await Promise.all(variantPromises);
        }

        // Ordina le varianti per priorità (0 = nessuna priorità, all'inizio)
        for (const productId in variantsByProduct) {
            variantsByProduct[productId].sort((a, b) => {
                const aPriority = a.priority || 0;
                const bPriority = b.priority || 0;

                // Se entrambi hanno priorità 0, mantieni l'ordine originale
                if (aPriority === 0 && bPriority === 0) {
                    return 0;
                }

                // Se solo uno ha priorità 0, mettilo all'inizio
                if (aPriority === 0) return -1;
                if (bPriority === 0) return 1;

                // Altrimenti ordina per priorità crescente
                return aPriority - bPriority;
            });
        }

        return variantsByProduct;
    } catch (error) {
        console.error("Error loading variants for products:", error);
        throw error;
    }
};

/**
 * Aggiorna la visibilità di un prodotto
 * @param {string} productId - ID del prodotto
 * @param {boolean} isVisible - Nuova visibilità
 * @returns {Promise<void>}
 */
export const updateProductVisibility = async (productId, isVisible) => {
    try {
        const productRef = doc(db, "CataloguesProducts", productId);
        await updateDoc(productRef, { isVisible });

        // Se il prodotto è impostato come non visibile, aggiorna anche tutte le sue varianti
        if (!isVisible) {
            const variantsQuery = query(
                collection(db, "CataloguesVariants"),
                where("productRef", "==", productId)
            );

            const variantsSnapshot = await getDocs(variantsQuery);

            const updatePromises = variantsSnapshot.docs.map(variantDoc => {
                return updateDoc(doc(db, "CataloguesVariants", variantDoc.id), { isVisible: false });
            });

            await Promise.all(updatePromises);
        }
    } catch (error) {
        console.error("Error updating product visibility:", error);
        throw error;
    }
};

/**
 * Aggiorna la visibilità di una variante
 * @param {string} variantId - ID della variante
 * @param {boolean} isVisible - Nuova visibilità
 * @returns {Promise<void>}
 */
export const updateVariantVisibility = async (variantId, isVisible) => {
    try {
        const variantRef = doc(db, "CataloguesVariants", variantId);
        await updateDoc(variantRef, { isVisible });
    } catch (error) {
        console.error("Error updating variant visibility:", error);
        throw error;
    }
};

/**
 * Salva il nuovo ordine dei prodotti
 * @param {Array} reorderedItems - Array dei prodotti riordinati
 * @returns {Promise<number>} Numero di prodotti aggiornati
 */
export const saveProductsOrder = async (reorderedItems) => {
    try {
        const updatePromises = [];

        // Aggiorna TUTTI i prodotti con la nuova priorità basata sull'indice + 1
        // Questo è necessario perché se i prodotti hanno priorità 0 di default,
        // dobbiamo riscrivere tutte le priorità per mantenere l'ordine corretto
        // Usiamo index + 1 per essere coerenti con quello che mostriamo nell'interfaccia
        reorderedItems.forEach((item, index) => {
            const productRef = doc(db, "CataloguesProducts", item.id);
            updatePromises.push(updateDoc(productRef, { priority: index + 1 }));
        });

        if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
        }

        return updatePromises.length;
    } catch (error) {
        console.error("Error saving new order:", error);
        throw error;
    }
};

/**
 * Salva il nuovo ordine delle varianti
 * @param {Array} reorderedItems - Array delle varianti riordinate
 * @returns {Promise<number>} Numero di varianti aggiornate
 */
export const saveVariantsOrder = async (reorderedItems) => {
    try {
        const updatePromises = [];

        // Aggiorna TUTTE le varianti con la nuova priorità basata sull'indice + 1
        // Questo è necessario perché se le varianti hanno priorità 0 di default,
        // dobbiamo riscrivere tutte le priorità per mantenere l'ordine corretto
        // Usiamo index + 1 per essere coerenti con quello che mostriamo nell'interfaccia
        reorderedItems.forEach((item, index) => {
            const variantRef = doc(db, "CataloguesVariants", item.id);
            updatePromises.push(updateDoc(variantRef, { priority: index + 1 }));
        });

        if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
        }

        return updatePromises.length;
    } catch (error) {
        console.error("Error saving new variant order:", error);
        throw error;
    }
};

/**
 * Elimina un prodotto dal catalogo e tutte le sue varianti
 * @param {string} productId - ID del prodotto da eliminare
 * @returns {Promise<{deletedVariants: number}>} Numero di varianti eliminate
 */
export const deleteProductFromCatalogue = async (productId) => {
    try {
        // 1. Recupera il prodotto per ottenere la lista delle varianti
        const productRef = doc(db, "CataloguesProducts", productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            throw new Error("Product not found");
        }

        const productData = productSnap.data();
        const variantIds = productData.list_variants || [];

        // 2. Elimina tutte le varianti associate
        const deleteVariantPromises = variantIds.map(variantId =>
            deleteDoc(doc(db, "CataloguesVariants", variantId))
        );

        await Promise.all(deleteVariantPromises);

        // 3. Elimina il prodotto
        await deleteDoc(productRef);

        return { deletedVariants: variantIds.length };
    } catch (error) {
        console.error("Error deleting product from catalogue:", error);
        throw error;
    }
};

/**
 * Elimina una variante dal catalogo e la rimuove dalla lista del prodotto
 * @param {string} variantId - ID della variante da eliminare
 * @param {string} productId - ID del prodotto che contiene la variante
 * @returns {Promise<void>}
 */
export const deleteVariantFromCatalogue = async (variantId, productId) => {
    try {
        // 1. Elimina la variante da CataloguesVariants
        await deleteDoc(doc(db, "CataloguesVariants", variantId));

        // 2. Rimuovi l'ID della variante dalla list_variants del prodotto
        const productRef = doc(db, "CataloguesProducts", productId);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
            const productData = productSnap.data();
            const updatedVariants = (productData.list_variants || []).filter(id => id !== variantId);

            await updateDoc(productRef, { list_variants: updatedVariants });
        }
    } catch (error) {
        console.error("Error deleting variant from catalogue:", error);
        throw error;
    }
};
